import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DataCacheContext = createContext(null);

/**
 * Cache global de colecciones consultadas con frecuencia.
 * - Primera carga: fetch en background al montar el provider (tras login)
 * - Navegación entre pestañas: lectura instantánea del cache
 * - CRUD: llama a refreshXxx() para actualizar el cache (y todas las pestañas lo reflejan)
 */
export const DataCacheProvider = ({ children }) => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [deliverers, setDeliverers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [ready, setReady] = useState({
    products: false, categories: false, clients: false, sellers: false, deliverers: false, packages: false, shipments: false
  });

  const refreshProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/products`, { withCredentials: true });
      // Orden alfabético por nombre (case-insensitive, respeta acentos en es-MX).
      // Los productos padre se ordenan; las variantes (con parent_product_id) se
      // re-anclan al final de cada grupo manteniendo orden alfabético por variant_name.
      const sorted = [...data].sort((a, b) => {
        const an = (a.name || '').toLowerCase();
        const bn = (b.name || '').toLowerCase();
        return an.localeCompare(bn, 'es-MX', { sensitivity: 'base' });
      });
      setProducts(sorted);
      setReady((r) => ({ ...r, products: true }));
      return sorted;
    } catch (e) { return products; }
  }, [products]);

  const refreshCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(data);
      setReady((r) => ({ ...r, categories: true }));
      return data;
    } catch (e) { return categories; }
  }, [categories]);

  const refreshClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data);
      setReady((r) => ({ ...r, clients: true }));
      return data;
    } catch (e) { return clients; }
  }, [clients]);

  const refreshSellers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sellers`, { withCredentials: true });
      setSellers(data);
      setReady((r) => ({ ...r, sellers: true }));
      return data;
    } catch (e) { return sellers; }
  }, [sellers]);

  const refreshDeliverers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/deliverers`, { withCredentials: true });
      setDeliverers(data);
      setReady((r) => ({ ...r, deliverers: true }));
      return data;
    } catch (e) { return deliverers; }
  }, [deliverers]);

  const refreshPackages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/packages`, { withCredentials: true });
      setPackages(data);
      setReady((r) => ({ ...r, packages: true }));
      return data;
    } catch (e) { return packages; }
  }, [packages]);

  const refreshShipments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/shipments`, { withCredentials: true });
      setShipments(data);
      setReady((r) => ({ ...r, shipments: true }));
      return data;
    } catch (e) { return shipments; }
  }, [shipments]);

  // Carga inicial en background cuando el usuario esté autenticado.
  useEffect(() => {
    if (!user) return;
    const loadAll = async () => {
      await Promise.all([
        refreshProducts(),
        refreshCategories(),
        refreshClients(),
        refreshSellers(),
        refreshDeliverers(),
        refreshPackages(),
        refreshShipments()
      ]);
    };
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const value = {
    products, categories, clients, sellers, deliverers, packages, shipments, ready,
    refreshProducts, refreshCategories, refreshClients, refreshSellers, refreshDeliverers, refreshPackages, refreshShipments
  };

  return <DataCacheContext.Provider value={value}>{children}</DataCacheContext.Provider>;
};

export const useDataCache = () => {
  const ctx = useContext(DataCacheContext);
  if (!ctx) throw new Error('useDataCache must be used inside DataCacheProvider');
  return ctx;
};
